import { FC, useEffect, useState } from 'react';

export type GaugeBand = {
  /** Upper bound (inclusive) of the score range this colour applies to. */
  maxValue: number;
  /** CSS colour for the arc and the number within this band. */
  colour: string;
};

type RatingGaugeProps = {
  /** Score 0-100, or null for the deactivated (empty payoff-hint) state. */
  value: number | null;
  /** Colour bands, ascending by maxValue. Falls back to the default grading. */
  bands?: GaugeBand[];
  /** Small text beneath the number. Defaults to "/ 100". */
  subtext?: string;
  /** Diameter in px. */
  size?: number;
  /** Animate the count-up + arc fill. */
  animate?: boolean;
  /** Delay before the count-up starts, in ms (used to stagger paired gauges). */
  delayMs?: number;
};

// Spec 5.4: <40 red, 40-70 amber, >70 green. Editable via Sanity; this is
// the fallback grading when no bands are configured.
const DEFAULT_BANDS: GaugeBand[] = [
  { maxValue: 39, colour: '#EF4444' },
  { maxValue: 70, colour: '#F59E0B' },
  { maxValue: 100, colour: '#22C55E' }
];

const TRACK_COLOUR = '#E9EBF0';
export const ANIMATION_DURATION_MS = 1200;
const ANIMATION_STEP_INTERVAL_MS = 16;

const resolveBandColour = (value: number, bands: GaugeBand[]): string => {
  const sorted = [...bands].sort((a, b) => a.maxValue - b.maxValue);
  const band = sorted.find((b) => value <= b.maxValue);
  return band?.colour ?? sorted[sorted.length - 1]?.colour ?? '#22C55E';
};

/**
 * Circular rating gauge shared across funnels (reveal + transfer steps).
 * Animates a count-up with the arc filling in step (CSS/SVG only, no libs).
 * Colour is graded by score band. A null value renders the deactivated
 * grey ring with no text — the empty payoff hint on the import step.
 */
const RatingGauge: FC<RatingGaugeProps> = ({
  value,
  bands,
  subtext = '/ 100',
  size = 180,
  animate = true,
  delayMs = 0
}) => {
  // Treat an absent OR empty bands array as "no config" — Sanity can return an
  // empty array (the schema encourages leaving it empty to use the defaults),
  // which would otherwise grade every score the single fallback colour.
  const effectiveBands = bands && bands.length > 0 ? bands : DEFAULT_BANDS;
  const strokeWidth = Math.round(size / 10);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  const isDeactivated = value === null;
  const target = value ?? 0;

  const [displayed, setDisplayed] = useState(animate ? 0 : target);

  useEffect(() => {
    if (isDeactivated) return;
    if (!animate) {
      setDisplayed(target);
      return;
    }

    const totalSteps = ANIMATION_DURATION_MS / ANIMATION_STEP_INTERVAL_MS;
    let currentStep = 0;
    setDisplayed(0);

    // Hold at 0 for `delayMs` before counting up — lets paired gauges stagger
    // (e.g. the transfer step fills NOW first, then WITH FFH).
    let interval: ReturnType<typeof setInterval> | undefined;
    const startTimeout = setTimeout(() => {
      interval = setInterval(() => {
        currentStep++;
        const progress = currentStep / totalSteps;
        const eased = 1 - Math.pow(1 - progress, 3);
        setDisplayed(Math.round(eased * target));

        if (currentStep >= totalSteps) {
          clearInterval(interval);
          setDisplayed(target);
        }
      }, ANIMATION_STEP_INTERVAL_MS);
    }, delayMs);

    return () => {
      clearTimeout(startTimeout);
      if (interval) clearInterval(interval);
    };
  }, [isDeactivated, animate, target, delayMs]);

  const colour = isDeactivated
    ? TRACK_COLOUR
    : resolveBandColour(displayed, effectiveBands);
  const strokeDashoffset = circumference - (displayed / 100) * circumference;

  return (
    <div
      className="relative mx-auto"
      style={{ width: size, height: size }}
      role="img"
      aria-label={
        isDeactivated ? 'Rating pending' : `Rating ${target} out of 100`
      }
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="-rotate-90"
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={TRACK_COLOUR}
          strokeWidth={strokeWidth}
        />
        {!isDeactivated && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={colour}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            style={{ transition: 'stroke-dashoffset 0.1s ease-out' }}
          />
        )}
      </svg>
      {!isDeactivated && (
        <div className="absolute inset-0 flex flex-col items-center justify-center font-body">
          <span
            className="text-5xl font-extrabold leading-none"
            style={{ color: colour }}
          >
            {displayed}
          </span>
          {subtext && (
            <span className="mt-1 text-sm font-semibold uppercase tracking-wide text-neutral-500">
              {subtext}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default RatingGauge;
